/**
 * TTS Manager - Handles both text-only and optional real-time TTS modes
 * Integrates with existing ChatController for seamless operation
 */
class TTSManager {
    constructor() {
        this.mode = 'text-only'; // 'text-only' or 'realtime-tts'
        this.realtimeTTS = null;
        this.isEnabled = false;
        this.currentVoiceId = null;
        this.elevenLabsApiKey = null;
        this.isInitialized = false;
        this.lipSyncController = null; // For real-time lip sync
    }

    /**
     * Initialize TTS Manager with configuration
     * @param {Object} config - Configuration object
     * @param {string} config.mode - 'text-only' or 'realtime-tts'
     * @param {string} config.elevenLabsApiKey - ElevenLabs API key (required for realtime-tts)
     * @param {string} config.voiceId - Default voice ID for TTS
     */
    async initialize(config = {}) {
        console.log('[TTSManager] Initializing with config:', config);
        
        this.mode = config.mode || 'text-only';
        this.elevenLabsApiKey = config.elevenLabsApiKey;
        this.currentVoiceId = config.voiceId;
        
        if (this.mode === 'realtime-tts') {
            if (!this.elevenLabsApiKey || !this.currentVoiceId) {
                console.warn('[TTSManager] Real-time TTS requires API key and voice ID. Falling back to text-only mode.');
                this.mode = 'text-only';
                this.isEnabled = false;
            } else {
                this.isEnabled = true;
                console.log('[TTSManager] Real-time TTS mode enabled');
            }
        } else {
            console.log('[TTSManager] Text-only mode enabled');
            this.isEnabled = false;
        }
        
        this.isInitialized = true;
    }

    /**
     * Called when an assistant turn begins
     */
    async onAssistantTurnStart() {
        if (!this.isInitialized || this.mode !== 'realtime-tts' || !this.isEnabled) {
            console.log('[TTSManager] Assistant turn start - text-only mode or TTS disabled');
            return;
        }

        try {
            // Create new real-time TTS instance for this turn
            this.realtimeTTS = new RealtimeLLMToElevenLabs({
                voiceId: this.currentVoiceId,
                elevenLabsApiKey: this.elevenLabsApiKey,
                modelId: "eleven_turbo_v2_5",
                outputFormat: "pcm_16000",
                syncAlignment: true,
                // Task #99: sentence-boundary outbound segmentation (engine defaults).
                jitterMs: 160
            });

            console.log('[TTSManager] Starting real-time TTS for assistant turn');
            await this.realtimeTTS.start();

            // Enable lip sync if we have a controller
            if (this.lipSyncController) {
                console.log('[TTSManager] Enabling real-time lip sync');
                this.realtimeTTS.enableLipSync(this.lipSyncController, {
                    frameMs: 33,
                    minRms: 0.015,
                    hissRatio: 1.35,
                    fftSize: 1024,
                });
            }
        } catch (error) {
            console.error('[TTSManager] Failed to start real-time TTS:', error);
            this.isEnabled = false;
        }
    }

    /**
     * Handle OpenAI streaming delta
     * @param {string} delta - Text chunk from OpenAI
     */
    onOpenAIDelta(delta) {
        if (!this.isInitialized || this.mode !== 'realtime-tts' || !this.isEnabled || !this.realtimeTTS) {
            return; // Text-only mode - no TTS processing
        }

        try {
            this.realtimeTTS.handleLLMDelta(delta);
        } catch (error) {
            console.error('[TTSManager] Error processing delta:', error);
        }
    }

    /**
     * Called when assistant turn ends
     */
    onAssistantTurnEnd() {
        if (!this.isInitialized || this.mode !== 'realtime-tts' || !this.isEnabled || !this.realtimeTTS) {
            console.log('[TTSManager] Assistant turn end - text-only mode or TTS disabled');
            return;
        }

        try {
            console.log('[TTSManager] Ending assistant turn, flushing remaining audio');
            this.realtimeTTS.flushAndClose();
            this.realtimeTTS = null;
        } catch (error) {
            console.error('[TTSManager] Error ending assistant turn:', error);
        }
    }

    /**
     * Called when user interrupts/barges in
     */
    onUserBargeIn() {
        if (!this.isInitialized || this.mode !== 'realtime-tts' || !this.realtimeTTS) {
            console.log('[TTSManager] User barge-in - text-only mode or no active TTS');
            return;
        }

        try {
            console.log('[TTSManager] User barge-in detected, canceling TTS');
            this.realtimeTTS.cancel();
            this.realtimeTTS = null;
        } catch (error) {
            console.error('[TTSManager] Error handling user barge-in:', error);
        }
    }

    /**
     * Set voice ID for current character
     * @param {string} voiceId - ElevenLabs voice ID
     */
    setVoiceId(voiceId) {
        console.log(`[TTSManager] Setting voice ID to: ${voiceId}`);
        this.currentVoiceId = voiceId;
        
        if (this.realtimeTTS) {
            this.realtimeTTS.setVoiceId(voiceId);
        }
    }

    /**
     * Set lip sync controller for real-time lip sync
     * @param {Object} lipSyncController - LipSyncController instance
     */
    setLipSyncController(lipSyncController) {
        console.log('[TTSManager] Setting lip sync controller');
        this.lipSyncController = lipSyncController;
    }

    /**
     * Toggle TTS mode
     * @param {string} mode - 'text-only' or 'realtime-tts'
     */
    setMode(mode) {
        if (mode === this.mode) return;
        
        console.log(`[TTSManager] Switching mode from ${this.mode} to ${mode}`);
        
        // Cancel any active TTS
        if (this.realtimeTTS) {
            this.realtimeTTS.cancel();
            this.realtimeTTS = null;
        }
        
        this.mode = mode;
        this.isEnabled = (mode === 'realtime-tts' && this.elevenLabsApiKey && this.currentVoiceId);
    }

    /**
     * Get current status
     */
    getStatus() {
        return {
            mode: this.mode,
            isEnabled: this.isEnabled,
            isInitialized: this.isInitialized,
            hasActiveSession: !!this.realtimeTTS,
            voiceId: this.currentVoiceId
        };
    }
}

// Create global instance
window.ttsManager = new TTSManager();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = TTSManager;
}